import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { cn } from "@/lib/utils"

// ═══════════════════════════════════════════════════════════════
// PREMIUM SLIDER - Luksusowy suwak z mikro-interakcjami
// ═══════════════════════════════════════════════════════════════

const PremiumSlider = React.forwardRef(({ 
  className, 
  showValue = true,
  showTicks = true,
  showTooltip = true,
  min = 0,
  max = 100,
  step = 1,
  value,
  onValueChange,
  formatValue,
  tickLabels,
  ...props 
}, ref) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value || [min]);
  
  const currentValue = value || localValue;
  const numSteps = Math.floor((max - min) / step) + 1;
  const tickPositions = Array.from({ length: numSteps }, (_, i) => min + i * step);
  
  // Haptic feedback (if available)
  const triggerHaptic = () => {
    if (navigator.vibrate) {
      navigator.vibrate(5);
    }
  };

  const handleValueChange = (newValue) => {
    triggerHaptic();
    setLocalValue(newValue);
    onValueChange?.(newValue);
  };

  const displayValue = formatValue 
    ? formatValue(currentValue[0]) 
    : `${currentValue[0]}/${max}`;

  const progressPercent = ((currentValue[0] - min) / (max - min)) * 100;

  return (
    <div className="w-full space-y-3">
      {/* Value display above slider */}
      {showValue && (
        <div className="flex justify-between items-center px-1">
          <span className="text-xs text-slate-400 font-medium">
            {tickLabels?.[0] || min}
          </span>
          <span 
            className={cn(
              "text-sm font-bold px-3 py-1 rounded-full transition-all duration-200",
              isDragging 
                ? "bg-sky-500 text-white scale-110 shadow-lg" 
                : "bg-slate-100 text-slate-700"
            )}
          >
            {displayValue}
          </span>
          <span className="text-xs text-slate-400 font-medium">
            {tickLabels?.[tickLabels.length - 1] || max}
          </span>
        </div>
      )}

      {/* Slider container */}
      <div className="relative py-2">
        <SliderPrimitive.Root
          ref={ref}
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onValueChange={handleValueChange}
          onPointerDown={() => setIsDragging(true)}
          onPointerUp={() => setIsDragging(false)}
          className={cn(
            "relative flex w-full touch-none select-none items-center cursor-pointer",
            className
          )}
          {...props}
        >
          {/* Track background */}
          <SliderPrimitive.Track 
            className="relative h-3 w-full grow overflow-hidden rounded-full"
            style={{
              background: 'linear-gradient(180deg, #e2e8f0, #f1f5f9)',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {/* Progress fill */}
            <SliderPrimitive.Range 
              className="absolute h-full rounded-full transition-all duration-150"
              style={{
                background: 'linear-gradient(90deg, #0ea5e9, #06b6d4, #10b981)',
                boxShadow: '0 0 12px rgba(14,165,233,0.4)'
              }}
            />
          </SliderPrimitive.Track>
          
          {/* Premium Thumb */}
          <SliderPrimitive.Thumb 
            className={cn(
              "block rounded-full transition-all duration-200 ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50",
              isDragging ? "scale-125" : "hover:scale-110"
            )}
            style={{
              width: '28px',
              height: '28px',
              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
              border: '3px solid #0ea5e9',
              boxShadow: isDragging 
                ? '0 0 20px rgba(14,165,233,0.6), 0 4px 12px rgba(0,0,0,0.2)' 
                : '0 2px 8px rgba(0,0,0,0.15), inset 0 1px 2px rgba(255,255,255,0.8)'
            }}
            aria-label="Slider thumb"
          >
            {/* Inner glow indicator */}
            <div 
              className="w-full h-full rounded-full"
              style={{
                background: isDragging 
                  ? 'radial-gradient(circle at 30% 30%, rgba(14,165,233,0.3), transparent)'
                  : 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), transparent)'
              }}
            />
          </SliderPrimitive.Thumb>

          {/* Tooltip on drag */}
          {showTooltip && isDragging && (
            <div 
              className="absolute -top-10 px-3 py-1.5 rounded-lg text-sm font-bold text-white pointer-events-none animate-scaleIn"
              style={{
                left: `calc(${progressPercent}% - 24px)`,
                background: 'linear-gradient(145deg, #0ea5e9, #0284c7)',
                boxShadow: '0 4px 12px rgba(14,165,233,0.4)'
              }}
            >
              {displayValue}
              {/* Tooltip arrow */}
              <div 
                className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45"
                style={{ background: '#0284c7' }}
              />
            </div>
          )}
        </SliderPrimitive.Root>

        {/* Tick marks */}
        {showTicks && numSteps <= 10 && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none">
            <div className="relative h-3 mx-[14px]">
              {tickPositions.map((tick, idx) => {
                const position = ((tick - min) / (max - min)) * 100;
                const isActive = tick <= currentValue[0];
                const isCurrent = tick === currentValue[0];
                
                return (
                  <div
                    key={tick}
                    className={cn(
                      "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full transition-all duration-200",
                      isCurrent 
                        ? "w-2 h-2 bg-sky-500 shadow-lg" 
                        : isActive 
                          ? "w-1.5 h-1.5 bg-sky-400" 
                          : "w-1.5 h-1.5 bg-slate-300"
                    )}
                    style={{ 
                      left: `${position}%`,
                      boxShadow: isCurrent ? '0 0 8px rgba(14,165,233,0.6)' : 'none'
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Tick labels below (if provided and few steps) */}
      {tickLabels && tickLabels.length <= 10 && (
        <div className="relative h-6 mx-[14px]">
          {tickLabels.map((label, idx) => {
            const position = (idx / (tickLabels.length - 1)) * 100;
            const isActive = idx <= Math.round((currentValue[0] - min) / step);
            
            return (
              <span
                key={idx}
                className={cn(
                  "absolute -translate-x-1/2 text-xs font-medium transition-colors duration-200",
                  isActive ? "text-sky-600" : "text-slate-400"
                )}
                style={{ left: `${position}%` }}
              >
                {label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
});

PremiumSlider.displayName = "PremiumSlider";

// Original basic slider for backwards compatibility
const Slider = React.forwardRef(({ className, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn("relative flex w-full touch-none select-none items-center", className)}
    {...props}>
    <SliderPrimitive.Track
      className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20">
      <SliderPrimitive.Range className="absolute h-full bg-primary" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
  </SliderPrimitive.Root>
));
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider, PremiumSlider }
